<template>
  <div v-if="currentUserIsSuperAdmin() || hasModel3Clinic && hasStorePermission">
    <vs-popup id="custom-dialog-v2" title="Cancel Dispense Request" :active.sync="isCancelDialogOpen" button-close-hidden>
      <vs-card>
        <div
          class="w-12 h-12 mb-4 bg-yellow-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-opacity-75"
        >
          <feather-icon icon="InfoIcon" svgClasses="h-7 w-7" class="text-yellow-700" />
        </div>
        <p class="text-xl font-bold mb-4">Are you sure you want to cancel this request?</p>
        <p class="mb-10 text-muted text-lg">
          Cancelling this dispense request cannot be undone. The patient will no longer have access to this prescription.
        </p>
        <div class="flex flex-col justify-end w-full">
          <div class="flex gap-4 justify-end">
            <vs-button
              @click="isCancelDialogOpen = false"
              class="font-semibold px-6 py-4"
              type="flat"
            >
              No, Keep Request
            </vs-button>
            <vs-button
              @click="handleConfirmCancelOrder"
              class="px-8 py-4 rounded-lg font-semibold"
              color="primary"
              :disabled="isCancelling"
            >
              {{ isCancelling ? 'Cancelling Request...' :'Yes, Cancel Request' }}
            </vs-button>
          </div>
        </div>
      </vs-card>
    </vs-popup>
    <vs-sidebar position-right parent="body" color="primary" class="dispense-request-list overflow-y-auto" spacer v-model="active">
      <div class="px-4">
        <div class="flex justify-between items-center">
          <p class="text-2xl">Order Number: <span class="font-bold">{{ selectedOrder.dispenseNumber }}</span></p>
          <vs-button
            @click.prevent="active=false"
            color="primary"
            icon="clear"
            type="flat"
            size="large"
            ></vs-button
          >
        </div>
        <vs-divider></vs-divider>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3">
          <div>
            <p class="text-xs text-gray-600">Clinic Name</p>
            <p class="text-sm text-gray-700 font-semibold">{{ selectedOrder.clinicName }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Delivery Link</p>
            <p v-html="renderDeliveryLink(selectedOrder.deliveryLink, true)"></p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Delivery Address</p>
            <p class="text-sm text-gray-700 font-semibold">{{ displayAddress(selectedOrder)}}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Created At</p>
            <p class="text-sm text-gray-700 font-semibold">{{ selectedOrder.createdAt | date_formatter }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Patient Dispense Fee</p>
            <p class="text-sm text-gray-700 font-semibold">${{ displayPatientDispenseFee(selectedOrder.dispenseRequest) }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Total</p>
            <p class="text-sm text-gray-700 font-semibold">${{ displayPricevalue(selectedOrder.total) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Gst</p>
            <p class="text-sm text-gray-700 font-semibold">${{ displayPricevalue(selectedOrder.gst) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Order Notes</p>
            <p class="text-sm text-gray-700 font-semibold">{{ selectedOrder.orderNotes || 'NA' }}</p>
          </div>
        </div>
        <vs-divider></vs-divider>
        <p class="font-bold uppercase mb-2">Dispense Requests</p>
        <div class="flex flex-col gap-3">
          <div :key="requestIndex" v-for="(request, requestIndex) in selectedOrder.dispenseRequest">
            <vs-table>
              <template slot="header">
                <p class="text-sm font-semibold mb-2">
                  {{ request.patientName }}
                </p>
              </template>
              <template slot="thead">
                <vs-th width="40%">
                  <p class="text-sm">Item Name</p>
                </vs-th>
                <vs-th>
                  <p class="text-sm w-full">Price</p>
                </vs-th>
                <vs-th class="text-center">
                  <p class="text-sm w-full">Quantity</p>
                </vs-th>
                <vs-th class="text-center">
                  <p class="text-sm text-center w-full">GST</p>
                </vs-th>
              </template>

              <template>
                <vs-tr :key="requestItemIndex" v-for="(requestItem, requestItemIndex) in request.items" >
                  <vs-td :data="request.items[requestItemIndex].itemName">
                    <p class="text-sm">{{requestItem.itemName}}</p>
                  </vs-td>

                  <vs-td :data="request.items[requestItemIndex].price">
                    <p class="text-sm">${{displayPricevalue(requestItem.price)}}</p>
                  </vs-td>

                  <vs-td :data="request.items[requestItemIndex].quantity">
                    <p class="text-center text-sm">{{requestItem.quantity}}</p>
                  </vs-td>

                  <vs-td :data="request.items[requestItemIndex].authorisationNumber">
                    <p class="text-center text-sm">${{displayPricevalue(requestItem.gst || 0.00)}}</p>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </div>
      </div>
    </vs-sidebar>
    <vs-card>
      <template slot="header">
        <div class="px-2 py-1 flex justify-between items-center">
          <div>
            <h4>Dispense Requests</h4>
            <p class="text-sm text-gray-600">
              Manage the available dispense requests
            </p>
          </div>
          <div>
            <vs-button
              @click="handleCreateNewRequest"
              color="primary"
              icon="add"
              >Create New Request</vs-button
            >
          </div>
        </div>
      </template>
      <div v-if="currentUserIsSuperAdmin()" class="flex items-center mb-4">
        <div class="flex items-center w-full md:w-1/4 p-2">
          <vs-select
            placeholder="Select an Organization"
            width="100%"
            label="Organization"
            name="organization"
            v-model="dataTableParams.organizationId"
            autocomplete
          >
            <vs-select-item
              :key="index"
              :value="item._id"
              :text="item.name"
              v-for="(item, index) in organizations"
            />
          </vs-select>
        </div>
        <div class="flex items-center w-full md:w-1/5 p-2">
          <vs-select
            width="100%"
            label="Clinic"
            name="clinic"
            placeholder="Select a Clinic"
            v-model="dataTableParams.clinicId"
            autocomplete
            :disabled="!dataTableParams.organizationId"
          >
            <vs-select-item
              :key="index"
              :value="item.clinicId"
              :text="item.name"
              v-for="(item, index) in clinicsList"
            />
          </vs-select>
        </div>
        <div class="flex items-center w-full md:w-1/5 p-2 mt-4">
          <vs-button
            @click="handleClearFilter"
            class="py-3"
            color="primary"
            :disabled="!(dataTableParams.organizationId || dataTableParams.clinicId)"
          >
            Clear Filter
          </vs-button>
        </div>
      </div>
      <div id="data-list-list-view" class="data-list-container">
        <vs-table ref="table" :total="totalDocs" :data="dispenseRequests">
          <template slot="thead">
            <vs-th>Clinic</vs-th>
            <vs-th>Dispense #</vs-th>
            <vs-th>Total</vs-th>
            <vs-th>Delivery Link</vs-th>
            <vs-th>Created At <span class="text-gray-400">(D-M-Y)</span></vs-th>
            <vs-th v-if="currentUserIsSuperAdmin()">Status</vs-th>
            <vs-th>
              <p class="text-center">Actions</p>
            </vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr :data="order" :key="orderIndex" v-for="(order, orderIndex) in data">

              <vs-td :data="order.clinicName">
                {{order.clinicName }}
              </vs-td>
              <vs-td :data="order.dispenseNumber">
                {{order.dispenseNumber || "N/A" }}
              </vs-td>
              <vs-td :data="order.total">
                ${{ order.total ||  "0.00" }}
              </vs-td>
              <vs-td>
                <span v-html="renderDeliveryLink(order.deliveryLink)"></span>
              </vs-td>
              <vs-td :data="order.createdAt">
                <div>
                  {{ order.createdAt | date_formatter }}
                </div>
              </vs-td>
              <vs-td v-if="currentUserIsSuperAdmin()" :data="order.createdAt">
                <div>
                  <vs-chip :color="getChipColor(order.status)">
                    {{ order.status || fallbackStatus}}
                  </vs-chip>
                </div>
              </vs-td>
              <vs-td>
                <div class="flex gap-2">
                  <vs-button
                    @click.prevent.stop="handleSelectedOrder(order)"
                    type="border"
                    size="small"
                    >View Order</vs-button
                  >
                  <vs-button
                    color="#1BD179"
                    @click.prevent.stop="handleReOrder(order)"
                    v-if="currentUserIsSuperAdmin()"
                    type="border"
                    size="small"
                    >Re-Order
                  </vs-button>
                  <vs-button
                    v-if="currentUserIsSuperAdmin() && isCancellable(order.status)"
                    @click.prevent.stop="openCancelDialog(order)"
                    type="border"
                    size="small"
                    color="danger"
                  >Cancel Order</vs-button
                  >
                </div>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <div class="p-5 flex justify-between items-center">
          <span class="mr-2">
            {{
              dataTableParams.page * dataTableParams.limit -
              (dataTableParams.limit - 1)
            }}
            -
            {{
              totalDocs - dataTableParams.page * dataTableParams.limit > 0
                ? dataTableParams.page * dataTableParams.limit
                : totalDocs
            }}
            of {{ totalDocs }}
          </span>
          <div class="pagination-div mt-0" v-if="serverResponded">
            <paginate
              :page-count="totalPage"
              :click-handler="handleChangePage"
              class="pagination"
              :page-range="9"
              :prevText="'<'"
              :nextText="'>'"
            ></paginate>
          </div>
        </div>
      </div>
    </vs-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { formatPriceValue } from '../../../utils/format';
import permissionMixin from '@/mixins/permissionMixin';
import { QLD_MODELS } from '@/constants/compliance';
import { routePrefix } from '@/globalHelpers/routes';
import { currentUserIsSuperAdmin } from '@/globalHelpers/userRole';
import { DISPENSE_STATUS, DISPENSE_STATUS_COLORS } from '@/constants/dispenseRequest';

export default {
  name: 'DispenseRequestsList',
  mixins: [permissionMixin],
  data() {
    return {
      active:false,
      dispenseRequests: [],
      totalDocs: 0,
      page: 1,
      noDataText: '',
      currentPage: 1,
      dataTableParams: {
        page: 1,
        limit: 25,
        organizationId: null,
        userId: null,
        clinicId: null,
      },
      isCancelling: false,
      serverResponded: false,
      selectedOrder:{
        evermedPayload:{},
        dispenseRequest:[],
      },
      selectedCancelOrder: {},
      isCancelDialogOpen:false,
      clinicsList: [],
      organizations: [],
      orgTableParams: {
        search: '',
        sort: 'name',
        dir: 'asc',
        page: 1,
        limit: 999999,
        filter: 'Approved',
      },
    };
  },
  methods: {
    currentUserIsSuperAdmin,
    ...mapActions('dispenseRequest', [
      'getDispenseRequests',
      'cancelDispenseRequest',
    ]),
    ...mapActions('organization', ['fetchOrganizations', 'fetchOrganizationAllClinics']),
    async fetchOrganizationList() {
      const { data: { data }} = await this.fetchOrganizations(this.orgTableParams);
      this.organizations = data.docs;
    },
    async fetchClinics(orgId) {
      const { data: { data } } = await this.fetchOrganizationAllClinics(orgId);
      this.clinicsList = data;
      this.dataTableParams.clinicId = null;
      this.dataTableParams.organizationId = orgId;
    },
    async fetchList() {
      this.$vs.loading();
      const res = await this.getDispenseRequests(this.dataTableParams);
      this.$vs.loading.close();
      this.dispenseRequests = res.data.data.docs.map((item) => {
        return {
          ...item,
          total: formatPriceValue(item.total),
        };
      });
      this.totalDocs = res.data.data.pagination.total;
      this.page = res.data.data.pagination.page;
      this.currentPage = res.data.data.pagination.page;
      this.noDataText = 'No Data Available';
      this.serverResponded = true;
    },
    handleChangePage(page) {
      this.dataTableParams.page = page;
      this.fetchList();
    },
    handleSelectedOrder(order) {
      this.selectedOrder = order;
      this.active=true;
    },
    openCancelDialog(order) {
      this.selectedCancelOrder = order;
      this.isCancelDialogOpen=true;
    },
    handleCreateNewRequest() {
      this.$router.push({ name: `${routePrefix()}DispenseRequestCreate` });
    },
    handleReOrder(dispense) {
      this.$router.push({ name: `${routePrefix()}DispenseRequestCreate`, query: {id: dispense._id} });
    },
    getChipColor(status) {
      if (!status) return DISPENSE_STATUS_COLORS[DISPENSE_STATUS.DELIVERED];

      const transformedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      return DISPENSE_STATUS_COLORS[transformedStatus];
    },
    isCancellable(status) {
      const CANCELLABLE_STATUSES = [
        DISPENSE_STATUS.PENDING,
        DISPENSE_STATUS.PREPARING,
      ];

      return CANCELLABLE_STATUSES.includes(status);
    },
    async handleConfirmCancelOrder() {
      if (this.isCancelling) return;

      try {
        this.isCancelling = true;

        await this.cancelDispenseRequest({id: this.selectedCancelOrder._id});

        this.fetchList();

        this.$vs.notify({
          title: 'Success!',
          text: 'The dispense order has been cancelled successfully.',
          iconPack: 'feather',
          icon: 'icon-check',
          color: 'success',
        });
      } catch (error) {
        this.$vs.notify({
          title: 'Failed to Cancel Order',
          text: error.data && error.data.message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
        });
      } finally {
        this.isCancelDialogOpen = false;
        this.isCancelling = false;
      }
    },
    displayAddress(order) {
      const address = order && order.evermedPayload && order.evermedPayload.deliveryAddress;
      return address
        ? `${address.line1}, ${address.administrativeRegionCode} ${address.locality} (${address.postalCode})`
        : 'Loading address...';
    },
    displayPricevalue(amount) {
      return formatPriceValue(amount);
    },
    displayPatientDispenseFee(dispenseRequest) {
      if (!Array.isArray(dispenseRequest)) return formatPriceValue(0);

      const total = dispenseRequest.reduce((sum, dispense) => {
        return sum + (dispense.dispenseFee.price || 0);
      }, 0);

      return formatPriceValue(total);
    },
    renderDeliveryLink(link, isSelected = false) {
      if (link) {
        return `<a href="${link}" target="_blank" rel="noopener noreferrer">Track Delivery</a>`;
      }

      return `<span class="${isSelected && 'text-sm text-gray-700 font-semibold' || ''}">Unavailable</span>`;
    },
    handleClearFilter() {
      this.dataTableParams.clinicId = null;
      this.dataTableParams.organizationId = null;

      this.fetchList();
    },
  },
  watch: {
    'dataTableParams.clinicId': function (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchList();
      }
    },
    'dataTableParams.organizationId': function (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchClinics(newVal);
        this.fetchList();
      }
    },
    'dataTableParams.limit': function (newlimit, oldLimit) {
      if (newlimit !== oldLimit) {
        this.serverResponded = false;
        this.dataTableParams.page = 1;
        this.dataTableParams.limit = newlimit;
        this.fetchList();
      }
    },
    '$store.state.organization.selectedOrganizationActiveModels'(models) {
      if (!Array.isArray(models) ) return;

      const lowerCaseModels = models.map(m => m.toLowerCase());

      if (!lowerCaseModels.includes(QLD_MODELS.MODEL_3.toLowerCase())) {
        this.$router.replace('/org-owner/home');
      }
    },
    active(val) {
      if (!val) this.selectedOrder = {};
    },
  },
  computed: {
    totalPage: function () {
      return this.totalDocs / this.dataTableParams.limit >
        parseInt(this.totalDocs / this.dataTableParams.limit)
        ? parseInt(this.totalDocs / this.dataTableParams.limit) + 1
        : parseInt(this.totalDocs / this.dataTableParams.limit);
    },
    hasModel3Clinic() {
      const models = this.$store.state.organization.selectedOrganizationActiveModels;
      return Array.isArray(models) && models.includes('model 3');
    },
    fallbackStatus() {
      return DISPENSE_STATUS.DELIVERED;
    },
  },
  created() {
    this.noDataText = 'Loading...';
    let orgId = localStorage.getItem(
      `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`
    );

    if (!currentUserIsSuperAdmin()) {
      this.dataTableParams.organizationId = orgId;
      this.dataTableParams.userId = this.$store.state.AppActiveUser.id;
    }

    this.fetchList();

    this.fetchOrganizationList();
  },
};
</script>
<style lang="scss">
@import "@/assets/scss/vuexy/components/popup-dialogv2.scss";

.dispense-request-list {
  .vs-sidebar {
    max-width: 700px;
    z-index: 99999;

    .vs-sidebar--items {
      overflow: auto;
    }
  }
}

</style>
